/**
 * haritaService.js — Backend API versiyonu
 * Firestore yerine kendi sunucumuzu kullanıyor.
 * Canlı konum: SSE (Server-Sent Events)
 * Diğerleri: REST API
 */

import { apiRequest } from "./apiClient";
import { isDataSyncConnected } from "./dataSyncService";

/* ─── Yardımcı: merkezi auth/refresh akışı ─── */
async function apiFetch(path, options = {}) {
  return apiRequest(path, options);
}

/* ─── Yardımcı: mesafe (km) ─── */
const _mesafe = (lat1, lng1, lat2, lng2) => {
  const R = 6371, dL = ((lat2-lat1)*Math.PI)/180, dN = ((lng2-lng1)*Math.PI)/180;
  const a = Math.sin(dL/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dN/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
};

/* ─────────────────────────────────────────
   ORTAK YERLER
───────────────────────────────────────── */

export const yerEkleService = async ({ familyId, yer, ekleyenAd, ekleyenUid }) => {
  const data = await apiFetch(`/map/${familyId}/places`, {
    method: "POST",
    body: JSON.stringify({ ...yer, ekleyenAd, ekleyenUid }),
  });
  // bildirimAktif ise backend placeBildirimGeofence da döner
  return { place: data.place || null, bildirimGeofence: data.placeBildirimGeofence || null };
};

export const yerGuncelleService = async ({ familyId, yerId, guncel }) => {
  const data = await apiFetch(`/map/${familyId}/places/${yerId}`, {
    method: "PUT",
    body: JSON.stringify(guncel),
  });
  return data.place || null;
};

export const yerSilService = async ({ familyId, yerId }) => {
  await apiFetch(`/map/${familyId}/places/${yerId}`, { method: "DELETE" });
};

export const yerleriDinleService = (familyId, callback) => {
  let iptal = false;

  // Sadece ilk yüklemede fetch yapılır.
  // Sonraki güncellemeler SSE (konumlariDinleService → places_updated) ile gelir.
  const yukle = async () => {
    try {
      const data = await apiFetch(`/map/${familyId}/places`);
      if (!iptal) callback(data.places || []);
    } catch (e) {
      console.error("[haritaService] yerler yüklenemedi:", e.message);
      if (!iptal) callback([]);
    }
  };

  yukle();
  // Polling kaldırıldı — değişiklikler SSE push ile gelir

  return () => { iptal = true; };
};

/* ─────────────────────────────────────────
   KONUM PAYLAŞIMI — SSE ile canlı
───────────────────────────────────────── */

export const konumGuncelleService = async ({
  familyId, uid, lat, lng, adres,
  paylasimAcik, accuracy, speed, heading, source,
  shareUntil, timedShare, savePoint = false,
}) => {
  return apiFetch(`/map/${familyId}/location`, {
    method: "POST",
    body: JSON.stringify({
      uid, lat, lng, adres, paylasimAcik, accuracy, speed, heading,
      source: source || "web",
      shareUntil, timedShare,
      savePoint: !!savePoint,
    }),
  });
};

export const paylasimDurumGuncelleService = async ({
  familyId, uid, paylasimAcik, shareUntil, timedShare,
}) => {
  await apiFetch(`/map/${familyId}/location-share`, {
    method: "PATCH",
    body: JSON.stringify({ uid, paylasimAcik, shareUntil, timedShare }),
  });
};

// Canlı paylaşım restore edilirken mevcut güvenli alan + geofence kayıtlarını
// tek istekte native Region Monitoring / Geofencing API katmanına taşır.
// Polling yapmaz; aile veya oturum değiştiğinde bir kez çağrılır.
export const nativeBolgeListesiGetirService = async (familyId) => {
  const data = await apiFetch(`/map/${familyId}/native-regions`);
  return data.regions || [];
};

/* Ana family data-stream üzerinden canlı konum + olay dinle
 *
 * Bu servis tek bir SSE bağlantısı üzerinden şu mesaj tiplerini işler:
 *   - init          : ilk bağlantıda tüm konum verisi
 *   - update        : tek kullanıcı konum güncellemesi
 *   - places_updated     : yer eklendi/güncellendi/silindi
 *   - geofences_updated  : geofence eklendi/silindi
 *   - safe_zones_updated : güvenli alan eklendi/silindi
 *   - meeting_updated    : buluşma noktası değişti
 *   - sos_updated        : SOS başlatıldı/bitirildi
 *
 * Yerler, geofence, güvenli alan, buluşma ve SOS için polling tamamen
 * kaldırıldı. Değişiklik olduğunda backend SSE üzerinden push eder.
 *
 * Ana SSE bağlı değilken REST polling devreye girer. Böylece yeni sürüm aile
 * başına ikinci EventSource açmaz; ağ kesintisinde mevcut deneyim korunur.
 */
const locationChannels = new Map();
const LOCATION_FALLBACK_MS = 15 * 1000;
const LOCATION_FALLBACK_MAX_MS = 60 * 1000;

const createLocationChannel = (familyId) => {
  const familyKey = String(familyId);
  const channel = {
    familyId: familyKey,
    subscribers: new Set(),
    locations: {},
    hasSnapshot: false,
    destroyed: false,
    pollingTimer: null,
    pollingPromise: null,
    failureCount: 0,
    eventRevision: 0,
    sseConnected: isDataSyncConnected(familyKey),
  };
  const uygulamaGizli = () => typeof document !== "undefined" && document.hidden;

  const publishLocations = (locations) => {
    channel.locations = locations && typeof locations === "object" ? locations : {};
    channel.hasSnapshot = true;
    channel.subscribers.forEach((subscriber) => {
      try { subscriber.locationCallback(channel.locations); } catch (_) {}
    });
  };

  const pollingDurdur = () => {
    if (channel.pollingTimer) clearTimeout(channel.pollingTimer);
    channel.pollingTimer = null;
  };

  const sonrakiPollingPlanla = () => {
    pollingDurdur();
    if (channel.destroyed || channel.sseConnected || uygulamaGizli()) return;
    const baseMs = Math.min(
      LOCATION_FALLBACK_MAX_MS,
      LOCATION_FALLBACK_MS * (2 ** Math.min(channel.failureCount, 2))
    );
    const jitteredMs = Math.round(baseMs * (0.8 + (Math.random() * 0.4)));
    channel.pollingTimer = setTimeout(async () => {
      channel.pollingTimer = null;
      await pollingYap();
      sonrakiPollingPlanla();
    }, jitteredMs);
  };

  const pollingYap = () => {
    if (channel.destroyed || uygulamaGizli()) return Promise.resolve(null);
    if (channel.pollingPromise) return channel.pollingPromise;
    const revisionAtStart = channel.eventRevision;
    channel.pollingPromise = apiFetch(`/map/${familyKey}/locations`)
      .then((data) => {
        if (!channel.destroyed) {
          channel.failureCount = 0;
          const snapshot = data.locations || {};
          publishLocations(
            channel.eventRevision === revisionAtStart
              ? snapshot
              : { ...snapshot, ...(channel.locations || {}) }
          );
        }
        return data;
      })
      .catch(() => {
        channel.failureCount += 1;
        return null;
      })
      .finally(() => { channel.pollingPromise = null; });
    return channel.pollingPromise;
  };

  const pollingBaslat = ({ hemen = true } = {}) => {
    if (channel.destroyed || channel.pollingTimer || uygulamaGizli() || channel.sseConnected) return;
    if (hemen) {
      pollingYap().finally(sonrakiPollingPlanla);
    } else {
      sonrakiPollingPlanla();
    }
  };

  const notifySubscribers = (optionName, value) => {
    channel.subscribers.forEach((subscriber) => {
      const callback = subscriber.options?.[optionName];
      if (typeof callback === "function") {
        try { callback(value); } catch (_) {}
      }
    });
  };

  const handleMapEvent = (msg) => {
    if (!msg || typeof msg !== "object") return;
    if (msg.type === "update" && msg.uid && msg.location) {
      channel.eventRevision += 1;
      publishLocations({ ...(channel.locations || {}), [msg.uid]: msg.location });
    } else if (msg.type === "places_updated") {
      notifySubscribers("onPlacesUpdated", msg.places || []);
    } else if (msg.type === "geofences_updated") {
      notifySubscribers("onGeofencesUpdated", msg.geofences || []);
    } else if (msg.type === "safe_zones_updated") {
      notifySubscribers("onSafeZonesUpdated", msg.safeZones || []);
    } else if (msg.type === "meeting_updated") {
      notifySubscribers("onMeetingUpdated", msg.meetingPoint ?? null);
    } else if (msg.type === "sos_updated") {
      notifySubscribers("onSosUpdated", msg.alerts || []);
    }
  };

  channel.dataChangedHandler = (event) => {
    const detail = event?.detail;
    if (String(detail?.familyId || "") !== familyKey || detail?.resource !== "mapLive") return;
    handleMapEvent(detail.item);
  };
  channel.statusHandler = (event) => {
    const detail = event?.detail;
    if (String(detail?.familyId || "") !== familyKey) return;
    const wasConnected = channel.sseConnected;
    channel.sseConnected = detail.connected === true;
    if (channel.sseConnected) pollingDurdur();
    else pollingBaslat({ hemen: wasConnected });
  };
  channel.reconcileHandler = (event) => {
    if (String(event?.detail?.familyId || "") === familyKey) pollingYap();
  };
  channel.visibilityHandler = () => {
    if (uygulamaGizli()) {
      pollingDurdur();
    } else if (!channel.sseConnected) {
      pollingBaslat();
    }
  };
  channel.onlineHandler = () => {
    if (!channel.sseConnected && !uygulamaGizli()) {
      pollingDurdur();
      pollingBaslat();
    }
  };
  channel.destroy = () => {
    if (channel.destroyed) return;
    channel.destroyed = true;
    pollingDurdur();
    window.removeEventListener("fp:dataChanged", channel.dataChangedHandler);
    window.removeEventListener("fp:sseStatus", channel.statusHandler);
    window.removeEventListener("fp:dataReconcile", channel.reconcileHandler);
    window.removeEventListener("online", channel.onlineHandler);
    document.removeEventListener("visibilitychange", channel.visibilityHandler);
    if (locationChannels.get(familyKey) === channel) locationChannels.delete(familyKey);
  };

  window.addEventListener("fp:dataChanged", channel.dataChangedHandler);
  window.addEventListener("fp:sseStatus", channel.statusHandler);
  window.addEventListener("fp:dataReconcile", channel.reconcileHandler);
  window.addEventListener("online", channel.onlineHandler);
  document.addEventListener("visibilitychange", channel.visibilityHandler);
  pollingYap().finally(() => {
    if (!channel.sseConnected) sonrakiPollingPlanla();
  });
  return channel;
};

export const konumlariDinleService = (familyId, locationCallback, options = {}) => {
  if (!familyId || typeof locationCallback !== "function") return () => {};
  const familyKey = String(familyId);
  let channel = locationChannels.get(familyKey);
  if (!channel) {
    channel = createLocationChannel(familyKey);
    locationChannels.set(familyKey, channel);
  }

  const subscriber = { locationCallback, options };
  let unsubscribed = false;
  channel.subscribers.add(subscriber);
  if (channel.hasSnapshot) {
    try { locationCallback(channel.locations); } catch (_) {}
  }

  return () => {
    if (unsubscribed) return;
    unsubscribed = true;
    channel.subscribers.delete(subscriber);
    if (channel.subscribers.size === 0) channel.destroy();
  };
};

/* ─────────────────────────────────────────
   COĞRAFİ SINIRLAR
───────────────────────────────────────── */

export const geofenceEkleService = async ({ familyId, geofence }) => {
  const data = await apiFetch(`/map/${familyId}/geofences`, {
    method: "POST",
    body: JSON.stringify(geofence),
  });
  return data.geofence || null;
};

export const geofenceSilService = async ({ familyId, geofenceId }) => {
  await apiFetch(`/map/${familyId}/geofences/${geofenceId}`, { method: "DELETE" });
};

export const geofencelariDinleService = (familyId, callback) => {
  let iptal = false;

  // Sadece ilk yüklemede fetch yapılır.
  // Sonraki güncellemeler SSE (konumlariDinleService → geofences_updated) ile gelir.
  const yukle = async () => {
    try {
      const data = await apiFetch(`/map/${familyId}/geofences`);
      if (!iptal) callback(data.geofences || []);
    } catch (e) {
      console.error("[haritaService] geofences yüklenemedi:", e.message);
      if (!iptal) callback([]);
    }
  };

  yukle();
  // Polling kaldırıldı — değişiklikler SSE push ile gelir

  return () => { iptal = true; };
};

/* ─────────────────────────────────────────
   BULUŞMA NOKTASI
───────────────────────────────────────── */

export const bulusmaNoktasiKaydetService = async ({ familyId, bulusma }) => {
  await apiFetch(`/map/${familyId}/meeting-point`, {
    method: "POST",
    body: JSON.stringify(bulusma),
  });
};

export const bulusmaNoktasiSilService = async ({ familyId }) => {
  await apiFetch(`/map/${familyId}/meeting-point`, { method: "DELETE" });
};

export const bulusmaNoktasiDinleService = (familyId, callback) => {
  let iptal = false;

  // Sadece ilk yüklemede fetch yapılır.
  // Sonraki güncellemeler SSE (konumlariDinleService → meeting_updated) ile gelir.
  const yukle = async () => {
    try {
      const data = await apiFetch(`/map/${familyId}/meeting-point`);
      if (!iptal) callback(data.meetingPoint || null);
    } catch (e) {
      console.error("[haritaService] bulusma yüklenemedi:", e.message);
      if (!iptal) callback(null);
    }
  };

  yukle();
  // Polling kaldırıldı — değişiklikler SSE push ile gelir

  return () => { iptal = true; };
};

/* ─────────────────────────────────────────
   SOS / ACİL KONUM
───────────────────────────────────────── */

export const sosGonderService = async ({ familyId, uid, lat, lng, tamAd }) => {
  await apiFetch(`/map/${familyId}/sos`, {
    method: "POST",
    body: JSON.stringify({ uid, lat, lng, tamAd }),
  });
};

export const sosBitirService = async ({ familyId, uid }) => {
  await apiFetch(`/map/${familyId}/sos/${uid}/end`, { method: "PATCH" });
};

export const sosAlertlariDinleService = (familyId, callback) => {
  let iptal = false;

  // Sadece ilk yüklemede fetch yapılır.
  // SOS başlatıldığında/bitirildiğinde backend SSE push eder
  // (konumlariDinleService → sos_updated). Polling gerekmez.
  const yukle = async () => {
    try {
      const data = await apiFetch(`/map/${familyId}/sos`);
      if (!iptal) callback(data.alerts || []);
    } catch (e) {
      console.error("[haritaService] sos yüklenemedi:", e.message);
      if (!iptal) callback([]);
    }
  };

  yukle();
  // Polling kaldırıldı — SOS değişiklikleri SSE push ile anlık gelir

  return () => { iptal = true; };
};

/* ─────────────────────────────────────────
   KONUM GEÇMİŞİ
───────────────────────────────────────── */

const _lastSnapshot = {};

const _snapMesafe = (lat1, lng1, lat2, lng2) => {
  const R = 6371, dL = ((lat2-lat1)*Math.PI)/180, dN = ((lng2-lng1)*Math.PI)/180;
  const a = Math.sin(dL/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dN/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
};

export const konumSnapshotKaydetService = async ({ familyId, uid, lat, lng, tamAd }) => {
  /* 50m filtresi — backend'de de kontrol var ama client'ta da filtrele */
  const key = `${familyId}_${uid}`;
  const prev = _lastSnapshot[key];
  if (prev) {
    const km = _snapMesafe(Number(prev.lat), Number(prev.lng), Number(lat), Number(lng));
    if (km * 1000 < 50) return;
  }
  _lastSnapshot[key] = { lat, lng };

  await apiFetch(`/map/${familyId}/location-history`, {
    method: "POST",
    body: JSON.stringify({ uid, lat, lng, tamAd, source: "snapshot" }),
  });
};

export const konumGecmisiniDinleService = (familyId, uid, callback, tarih = null) => {
  const hedefTarih = tarih || new Date().toISOString().slice(0, 10);
  let iptal = false;

  const yukle = async () => {
    try {
      const data = await apiFetch(`/map/${familyId}/location-history/${uid}?date=${hedefTarih}`);
      if (!iptal) callback(data.points || []);
    } catch (e) {
      console.error("[haritaService] gecmis yüklenemedi:", e.message);
      if (!iptal) callback([]);
    }
  };

  yukle();

  return () => { iptal = true; };
};

export const konumGecmisiniSilService = async ({ familyId }) => {
  await apiFetch(`/map/${familyId}/location-history/me`, { method: "DELETE" });
};

export const bugunKonumGecmisiniGetirService = async ({ familyId, uid }) => {
  const bugun = new Date().toISOString().slice(0, 10);
  const data = await apiFetch(`/map/${familyId}/location-history/${uid}?date=${bugun}`);
  return data.points || [];
};

/* ─────────────────────────────────────────
   GÜVENLİ ALAN
───────────────────────────────────────── */

export const guvenliAlanEkleService = async ({ familyId, alan }) => {
  const data = await apiFetch(`/map/${familyId}/safe-zones`, {
    method: "POST",
    body: JSON.stringify(alan),
  });
  return data.safeZone || null;
};

export const guvenliAlanSilService = async ({ familyId, alanId }) => {
  await apiFetch(`/map/${familyId}/safe-zones/${alanId}`, { method: "DELETE" });
};

export const guvenliAlanlariDinleService = (familyId, callback) => {
  let iptal = false;

  // Sadece ilk yüklemede fetch yapılır.
  // Sonraki güncellemeler SSE (konumlariDinleService → safe_zones_updated) ile gelir.
  const yukle = async () => {
    try {
      const data = await apiFetch(`/map/${familyId}/safe-zones`);
      if (!iptal) callback(data.safeZones || []);
    } catch (e) {
      console.error("[haritaService] guvenliAlanlar yüklenemedi:", e.message);
      if (!iptal) callback([]);
    }
  };

  yukle();
  // Polling kaldırıldı — değişiklikler SSE push ile gelir

  return () => { iptal = true; };
};

/* ─────────────────────────────────────────
   BÖLGE DURUM TAKİBİ
───────────────────────────────────────── */

// Local cache — backend'e sadece durum değişince istek at
const _zoneStateCache = new Map();

export const bolgeDurumuGecisKaydetService = async ({
  familyId, zoneType, zoneId, uid, durum, mesafeMetre,
}) => {
  const cacheKey = `${familyId}_${zoneType}_${zoneId}_${uid}`;
  const onceki = _zoneStateCache.get(cacheKey);

  // Durum değişmediyse backend'e istek atma
  if (onceki === durum) {
    return { changed: false, ilkKayit: false, onceki, yeni: durum };
  }

  const ilkKayit = onceki === undefined;
  _zoneStateCache.set(cacheKey, durum);

  // İlk kayıtta backend'e yaz ama changed=false döndür (bildirim gönderme)
  try {
    await apiFetch(`/map/${familyId}/zone-state`, {
      method: "POST",
      body: JSON.stringify({ uid, zoneType, zoneId, durum, mesafeMetre }),
    });
  } catch (e) {
    console.warn("[haritaService] zoneState hatası:", e.message);
  }

  return { changed: !ilkKayit, ilkKayit, onceki: onceki ?? null, yeni: durum };
};

/* ─────────────────────────────────────────
   HAREKET ANALİZİ
───────────────────────────────────────── */

export const hareketAnaliziGetirService = async ({ familyId, uid, gun = 7 }) => {
  try {
    // Timezone offset — backend UTC, biz yerel tarihle gösteriyoruz
    const tzOffset = -new Date().getTimezoneOffset(); // TR: +180
    const data = await apiFetch(`/map/${familyId}/movement-analysis/${uid}?days=${gun}&tzOffset=${tzOffset}`);
    return data.analysis || [];
  } catch (e) {
    console.error("[haritaService] analiz hatası:", e.message);
    return [];
  }
};
